import tinycolor from 'tinycolor2'

import { EMPTY_STRING } from '../../../constants/common'
import { COLOR_FORMAT_RGB } from '../ColorPicker.constants'

function normalizeRgbInput(value) {
  if (!value) {
    return EMPTY_STRING
  }

  return String(value)
    .trim()
    .replace(/^rgb\s*\(/i, EMPTY_STRING)
    .replace(/\)$/, EMPTY_STRING)
    .replace(/\s+/g, ' ')
}

export function normalizeHexColor(value) {
  if (!value) {
    return EMPTY_STRING
  }

  const preparedValue = value.startsWith('#') ? value : `#${value}`
  const color = tinycolor(preparedValue)

  if (!color.isValid()) {
    return preparedValue.toUpperCase()
  }

  return color.toHexString().toUpperCase()
}

export function isValidHexColor(value) {
  return /^#([0-9A-F]{6})$/.test(value)
}

export function parseStrictHexManualInput(value) {
  const trimmed = value?.trim()

  if (!trimmed) {
    return null
  }

  const colorValue = trimmed.startsWith('#') ? trimmed.slice(1) : trimmed

  if (!/^[0-9A-Fa-f]{6}$/.test(colorValue)) {
    return null
  }

  const withHash = `#${colorValue}`
  const color = tinycolor(withHash)

  if (!color.isValid()) {
    return null
  }

  return color.toHexString().toUpperCase()
}

export function getNormalizedFieldValue({ color, infoTextKey, textKey }) {
  return {
    color: normalizeHexColor(color),
    infoTextKey: infoTextKey,
    textKey: textKey,
  }
}

export function getRgbStringFromHex(hex) {
  if (!hex) {
    return EMPTY_STRING
  }

  const rgb = tinycolor(hex).toRgb()

  return `${rgb.r}  ${rgb.g}  ${rgb.b}`
}

export function getDisplayColorValue(color, format) {
  if (!color) {
    return EMPTY_STRING
  }

  if (format === COLOR_FORMAT_RGB) {
    return getRgbStringFromHex(color)
  }

  return normalizeHexColor(color)
}

export function parseRgbString(value) {
  const normalizedValue = normalizeRgbInput(value)

  if (!normalizedValue) {
    return null
  }

  const parts = normalizedValue
    .split(' ')
    .map(item => item.trim())
    .filter(Boolean)

  if (parts.length !== 3) {
    return null
  }

  const channels = parts.map(Number)

  function isValidChannel(channel) {
    return Number.isInteger(channel) && channel >= 0 && channel <= 255
  }

  if (!channels.every(isValidChannel)) {
    return null
  }

  const [r, g, b] = channels

  return tinycolor({ b, g, r }).toHexString().toUpperCase()
}

export function formatRgbInput(value) {
  const parsedColor = parseRgbString(value)

  if (!parsedColor) {
    return value
  }

  return getRgbStringFromHex(parsedColor)
}

export function parseColorByFormat(value, format) {
  if (format === COLOR_FORMAT_RGB) {
    return parseRgbString(value)
  }

  return parseStrictHexManualInput(value)
}

// The field input can be much wider than the popover (e.g. inside a wide
// modal). Anchoring `right`/`left` fallback placements to its full width
// would push the popover past the field entirely instead of over it, so
// floating-ui gets a virtual reference narrowed to the field's start.
export function getFieldPositionReference(node, width) {
  return {
    getBoundingClientRect() {
      const rect = node.getBoundingClientRect()
      const referenceWidth = Math.min(rect.width, width)

      // `DOMRect` properties are inherited getters, not own enumerable
      // properties, so they must be listed explicitly instead of spread.
      return {
        bottom: rect.bottom,
        height: rect.height,
        left: rect.left,
        right: rect.left + referenceWidth,
        top: rect.top,
        width: referenceWidth,
        x: rect.x,
        y: rect.y,
      }
    },
  }
}
